import { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import useSellerPromotions from './useSellerPromotions';
import useDiscountCodes from './useDiscountCodes';
import useDiscountCodeToMsgConverter from './useDiscountCodeToMsgConverter';
import useSellerPromoToMsgConverter from './useSellerPromoToMsgConverter';
import { messageIsReadUpdatesState } from '../state';

const INITIAL_RECORDS = 2;
const RECORDS_PER_PAGE = 10;

/**
 * Hook for OffersMessageList
 */
const useOffersMessageList = ({ inDropdown = false, enabled = true } = {}) => {
  const [curPage, setCurPage] = useState(0);
  const messageIsReadUpdates = useRecoilValue(messageIsReadUpdatesState);

  const {
    data: sellerPromotions,
    error: sellerPromotionsError,
    loading: sellerPromotionsLoading,
  } = useSellerPromotions({ enabled });

  const formatSellerPromotion = useSellerPromoToMsgConverter();

  const {
    data: discountCodes,
    error: discountCodesError,
    loading: discountCodesLoading,
  } = useDiscountCodes({ enabled });

  const loading = sellerPromotionsLoading || discountCodesLoading;
  const error = sellerPromotionsError || discountCodesError;

  const formatDiscountCode = useDiscountCodeToMsgConverter();

  const sellerPromoMessages = useMemo(
    () =>
      (!sellerPromotionsError &&
        !sellerPromotionsLoading &&
        sellerPromotions.map(formatSellerPromotion).filter(Boolean)) ||
      [],
    [
      formatSellerPromotion,
      sellerPromotions,
      sellerPromotionsError,
      sellerPromotionsLoading,
    ],
  );

  const discountCodeMessages = useMemo(
    () =>
      (!discountCodesError &&
        !discountCodesLoading &&
        discountCodes.map(formatDiscountCode).filter(Boolean)) ||
      [],
    [
      discountCodes,
      discountCodesError,
      discountCodesLoading,
      formatDiscountCode,
    ],
  );

  const filteredMessages = useMemo(() => {
    const groupedOfferMessages = [
      ...sellerPromoMessages,
      ...discountCodeMessages,
    ];

    return groupedOfferMessages.filter(
      (message) =>
        message?.node?.id &&
        // filter read messages for dropdown
        (!inDropdown ||
          (!message?.node?.isRead &&
            !messageIsReadUpdates.includes(message?.node?.id))),
    );
  }, [
    sellerPromoMessages,
    discountCodeMessages,
    inDropdown,
    messageIsReadUpdates,
  ]);

  const allOfferMessages = useMemo(
    () =>
      filteredMessages.sort(
        (a, b) =>
          new Date(b?.node?.deliveredAt || '').getTime() -
          new Date(a?.node?.deliveredAt || '').getTime(),
      ),
    [filteredMessages],
  );

  const paginatedMessages = useMemo(() => {
    const recordsToShow =
      INITIAL_RECORDS + (inDropdown ? 0 : curPage) * RECORDS_PER_PAGE;

    return allOfferMessages.slice(0, recordsToShow);
  }, [allOfferMessages, curPage, inDropdown]);

  const onFetchNextRecords = () => {
    setCurPage(curPage + 1);
  };

  return {
    loading,
    error,
    offerMessages: paginatedMessages,
    onFetchNextRecords,
    hasNextPage: paginatedMessages.length < allOfferMessages.length,
    totalRecords: allOfferMessages.length,
  };
};

export default useOffersMessageList;
